import React from "react";

import JsonView from "@uiw/react-json-view";
import { twMerge } from "tailwind-merge";

import { GenericObject } from "src/api/flowTypes";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { jsonStringSpeakPython } from "src/utils/speakPython";

type Props = {
  dataLoc?: string;
  containerRef?:
    | React.RefObject<HTMLDivElement | null>
    | ((div: HTMLDivElement) => void);
  jsonObject: GenericObject;
  speakPython?: boolean;
  innerDimensionClass?: string;
  defaultInspectDepth?: number;
  enableClipboard?: boolean;
};

const THEME = {
  // Inherit the container's font-code-12 instead of the default Menlo 13px
  fontFamily: "inherit",
  fontSize: "inherit",
  lineHeight: "inherit",
  "--w-rjv-background-color": "transparent",
  "--w-rjv-line-color": "#E5E6EB",
  "--w-rjv-color": "#000000",
  "--w-rjv-key-string": "#000000",
  "--w-rjv-key-number": "#098658",
  "--w-rjv-quotes-color": "#000000",
  "--w-rjv-curlybraces-color": "#000000",
  "--w-rjv-colon-color": "#000000",
  "--w-rjv-brackets-color": "#000000",
  "--w-rjv-ellipsis-color": "#000000",
  "--w-rjv-arrow-color": "#000000",
  "--w-rjv-type-string-color": "#a31515",
  "--w-rjv-quotes-string-color": "#a31515",
  "--w-rjv-type-int-color": "#098658",
  "--w-rjv-type-float-color": "#098658",
  "--w-rjv-type-boolean-color": "#0000ff",
  "--w-rjv-type-null-color": "#0000ff",
} as React.CSSProperties;

const toSpeakPythonCopyText = (value: unknown): string => {
  try {
    return jsonStringSpeakPython(JSON.stringify(value));
  } catch {
    return String(value);
  }
};

export const ObjectDetailPane: React.FC<Props> = ({
  dataLoc,
  containerRef,
  jsonObject,
  speakPython = true,
  innerDimensionClass,
  defaultInspectDepth = 3,
  enableClipboard = true,
}) => {
  return (
    <div
      ref={containerRef}
      className={twMerge(
        "decideScrollbar overflow-auto rounded-lg border border-gray-200 bg-gray-50 bg-opacity-50 p-2 font-code-12",
        innerDimensionClass,
      )}
      data-loc={dataLoc}
    >
      <JsonView
        collapsed={defaultInspectDepth}
        displayDataTypes={false}
        displayObjectSize={false}
        enableClipboard={enableClipboard}
        highlightUpdates={false}
        shortenTextAfterLength={50}
        style={THEME}
        value={jsonObject}
      >
        <JsonView.Colon>: </JsonView.Colon>
        <JsonView.Ellipsis
          // data-expanded is inverted in @uiw/react-json-view: true = collapsed
          // (the lib itself only renders the default ellipsis when it is set)
          render={({ "data-expanded": isCollapsed, ...props }, { value }) =>
            isCollapsed && value && Object.keys(value).length > 0 ? (
              <span {...props}> … </span>
            ) : undefined
          }
        />
        <JsonView.Copied
          render={(_props, { value }) => (
            <span className="-my-1 inline-flex align-middle">
              <CopyTextIcon
                dataLoc="copy-json-value"
                feedback="inline-only-checkmark"
                size="2xs"
                value={() => toSpeakPythonCopyText(value)}
              />
            </span>
          )}
        />
        {speakPython && (
          <>
            <JsonView.True
              render={(props, { type }) =>
                type === "value" ? <span {...props}>True</span> : undefined
              }
            />
            <JsonView.False
              render={(props, { type }) =>
                type === "value" ? <span {...props}>False</span> : undefined
              }
            />
            <JsonView.Null
              render={(props, { type }) =>
                type === "value" ? <span {...props}>None</span> : undefined
              }
            />
          </>
        )}
      </JsonView>
    </div>
  );
};
